import type { CardPricePoint, CardSummary } from "@hobbyshopify/backend-client";
import { BaseController, type SubtreeModel } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import { loadPriceHistory } from "../../core/ops/cards-ops";
import { Loader } from "../../core/primitives/loader";
import { PriceHistoryActions, PriceHistoryState } from "./price-history-model";

export type PriceHistoryDeps = Pick<AppDeps, "client" | "authHandler" | "storeRepository">;

/**
 * The recorded price history for one card, as a dialog a page owns for its
 * lifetime. The series belongs to the card rather than to whichever scan
 * observed it, so every copy and every offer of that card reads one history.
 */
export class PriceHistoryController extends BaseController implements PriceHistoryActions {
  readonly state = new PriceHistoryState();
  /** Closing, or reopening for another card, supersedes a load still in flight. */
  readonly #prices: Loader<{ shopId: string; cardId: string }, CardPricePoint[]>;

  constructor(
    subtree: SubtreeModel,
    readonly deps: PriceHistoryDeps,
  ) {
    super();
    subtree.put(PriceHistoryState, this.state);
    subtree.put(PriceHistoryActions, this);
    this.#prices = this.own(
      new Loader({
        status: this.state.status,
        fetch: (o: { shopId: string; cardId: string }) => loadPriceHistory({ ...deps, ...o }),
        loaded: (prices) => {
          this.state.prices.value = prices;
        },
      }),
    );
  }

  open = async (card: CardSummary) => {
    const shopId = this.deps.storeRepository.shopId;
    if (shopId === null) return;
    this.state.card.value = card;
    this.state.prices.value = [];
    await this.#prices.load({ shopId, cardId: card.id });
  };

  close = () => {
    this.state.card.value = null;
    this.state.prices.value = [];
    this.#prices.reset();
  };
}
